import React from "react";
import { useTranslation } from "react-i18next";
import {
  Row,
  Col,
  Space,
  Form,
  Modal,
  Popconfirm,
  Tooltip,
  Button,
} from "antd";
import {
  EditOutlined,
  CloseOutlined,
  UndoOutlined,
  ExportOutlined,
} from "@ant-design/icons";
import { Link } from "react-router-dom";
import AdnPageTitle from "../common/adnPageTitle/AdnPageTitle";
import AdnTable from "../common/adnTable/AdnTable";
import AdnCard from "../common/adnCard/AdnCard";
import AdnDropdown from "../common/adnDropdown/AdnDropdown";
import AdnAutoComplete from "../common/adnAutoComplete/AdnAutoComplete";
import {
  dateFormat,
  NameValidation,
  OfferStatus,
  WorkPlaces,
  MonthsPortuguese,
} from "../common/GeneralConfigurations";
import AdnMonthYearDropdown from "../common/adnMonthYearDropdown/AdnMonthYearDropdown";
import { AppRoutes } from "../../AppRoutes";
import moment from "moment";
import AdnInput from "../common/adnInput/AdnInput";
import AdnDatePicker from "../common/adnDatePicker/AdnDatePicker";
import AdnTextArea from "../common/adnTextArea/AdnTextArea";
import { NoteValidation } from "../employee/EmployeeOfferValidations";
import "./OfferListForm.css";

const OfferListForm = ({
  employeeOfferList,
  currentPage,
  pageSize,
  handleChangePage,
  typeOfferList,
  offerTypeChange,
  offerStatusHandleChange,
  handleSearchDescription,
  handleSearchEmployeeName,
  loading,
  totalRecords,
  handleOrderList,
  handleMonthChange,
  handleYearChange,
  typeId,
  month,
  year,
  EditOfferHandler,
  editMode,
  ShowEditModal,
  CancelEditModal,
  formOffers,
  modalSubmitLoading,
  disabledDateOffer,
  handleChangeOffer,
  offerStatus,
  handleOfferDate,
  handleCancelOfferModal,
  cancelOfferVisible,
  setCancelOfferVisible,
  cancelOfferForm,
  offerFormValues,
  getRowClassName,
  handleChangeManager,
  managersList,
  handleEmployeeOffersExcel,
}) => {
  const { t } = useTranslation();
  const title = t("offer.form.title");

  const FirstLastName = (name) => {
    let fullName = name.split(" ");
    let firstName = fullName[0];
    return firstName.concat(" " + fullName[fullName.length - 1]);
  };

  const columns = [
    {
      title: t("offer.form.columns.consultant"),
      dataIndex: "employeeName",
      sorter: true,
      render: (text, record) => (
        <Link to={AppRoutes.consultantById.createPath(record.employeeId)}>
          {FirstLastName(text)}
        </Link>
      ),
    },
    {
      title: t("offer.form.columns.type"),
      dataIndex: "offerName",
    },
    {
      title: t("offer.form.columns.referenceDate"),
      dataIndex: "referenceDate",
      sorter: true,
      render: (text) =>
        text
          ? moment(text).format("DD") +
            " de " +
            MonthsPortuguese[parseInt(moment(text).format("MM"))] +
            " " +
            moment(text).format("YYYY")
          : undefined,
      width: "15%",
    },
    {
      title: t("offer.form.columns.description"),
      dataIndex: "description",
      sorter: true,
    },
    {
      title: t("offer.form.columns.location"),
      dataIndex: "workPlaceId",
      render: (text, record) => {
        const workPlaceName = Object.keys(WorkPlaces).find(
          (key) => WorkPlaces[key] === record.workPlaceId
        );
        if (workPlaceName) {
          const formattedName = workPlaceName.replace(
            /([a-z])([A-Z])/g,
            "$1 $2"
          );
          return formattedName;
        }
        return null;
      },
    },
    {
      title: t("offer.form.columns.manager"),
      dataIndex: "managerAbbreviature",
    },
    {
      title: t("offer.form.columns.offeredAt"),
      dataIndex: "offeredAt",
      sorter: true,
      render: (text, record) => (
        <Space style={{ width: "100%" }} size="small" className="date-cell">
          {text ? (
            <p>{moment(text).format(dateFormat)}</p>
          ) : record.status !== OfferStatus.Delivered &&
            record.status !== OfferStatus.NotDelivered ? (
            <p>-</p>
          ) : (
            <>
              <AdnDatePicker
                className="offer-date-picker"
                name={"offeredAt"}
                placeholder={t("offer.form.placeholders.selectDate")}
                disabledDate={disabledDateOffer}
                format={dateFormat}
                allowClear={true}
                onChange={(value) => {
                  handleOfferDate(value, record);
                }}
              />
            </>
          )}
        </Space>
      ),
    },
    {
      title: t("offer.form.columns.status"),
      dataIndex: "status",
      render: (text, record) =>
        record.status === OfferStatus.Delivered ? (
          <div className="state-tag tag-done">
            {t("offer.form.status.delivered")}
          </div>
        ) : record.status === OfferStatus.NotDelivered ? (
          <div className="state-tag tag-danger">
            {t("offer.form.status.notDelivered")}
          </div>
        ) : (
          <div className="state-tag tag-canceled">
            {t("offer.form.status.canceled")}
          </div>
        ),
    },
    {
      title: t("offer.form.columns.action"),
      key: "action",
      render: (record) => (
        <Space size="middle">
          {record.status === OfferStatus.NotDelivered ? (
            <>
              <Tooltip title={t("offer.form.tooltips.edit")}>
                <EditOutlined
                  style={{ color: "black" }}
                  onClick={() => {
                    formOffers.setFieldsValue(record);
                    ShowEditModal(record);
                  }}
                />
              </Tooltip>
              <Tooltip title={t("offer.form.tooltips.cancel")}>
                <CloseOutlined
                  style={{ color: "red" }}
                  onClick={() => handleCancelOfferModal(record)}
                ></CloseOutlined>
              </Tooltip>
            </>
          ) : (
            <>
              <Popconfirm
                title={t("offer.form.confirmations.resetOffer")}
                onConfirm={() => {
                  handleChangeOffer(record, OfferStatus.NotDelivered);
                }}
              >
                <Tooltip title={t("offer.form.tooltips.reset")}>
                  <UndoOutlined style={{ color: "blue" }} />
                </Tooltip>
              </Popconfirm>
            </>
          )}
        </Space>
      ),
      width: "10%",
    },
  ];

  return (
    <>
      <Row
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <AdnPageTitle title={title} />
      </Row>

      <AdnCard loading={loading}>
        <Row gutter={16} className="filter-group">
          <Col
            className="filter-item"
            xs={{ span: 24 }}
            sm={{ span: 24 }}
            md={{ span: 24 }}
            lg={{ span: 8 }}
            xl={{ span: 4 }}
            xxl={{ span: 3 }}
          >
            <AdnAutoComplete
              placeholder={t("offer.form.placeholders.searchConsultant")}
              resultList={[]}
              handleSearch={handleSearchEmployeeName}
            />
          </Col>
          <Col
            className="filter-item"
            xs={{ span: 24 }}
            sm={{ span: 24 }}
            md={{ span: 24 }}
            lg={{ span: 8 }}
            xl={{ span: 4 }}
            xxl={{ span: 3 }}
          >
            <AdnAutoComplete
              placeholder={t("offer.form.placeholders.searchDescription")}
              resultList={[]}
              handleSearch={handleSearchDescription}
            />
          </Col>
          <Col
            className="filter-item"
            xs={{ span: 24 }}
            sm={{ span: 24 }}
            md={{ span: 24 }}
            lg={{ span: 8 }}
            xl={{ span: 4 }}
            xxl={{ span: 3 }}
          >
            <AdnDropdown
              placeholder={t("offer.form.placeholders.offerType")}
              defaultValue={typeId !== "" ? typeId : null}
              allowClear={true}
              options={typeOfferList}
              handleChange={offerTypeChange}
            />
          </Col>
          <Col
            xs={{ span: 24 }}
            sm={{ span: 24 }}
            md={{ span: 24 }}
            lg={{ span: 8 }}
            xl={{ span: 4 }}
            xxl={{ span: 3 }}
          >
            <AdnDropdown
              name="managerId"
              type="text"
              rules={[
                {
                  required: true,
                  message: t("offer.form.validation.managerRequired"),
                },
              ]}
              placeholder={t("offer.form.placeholders.selectManager")}
              allowClear={true}
              options={managersList}
              handleChange={handleChangeManager}
              mode={"multiple"}
            />
          </Col>
          <Col
            className="filter-item"
            xs={{ span: 24 }}
            sm={{ span: 24 }}
            md={{ span: 24 }}
            lg={{ span: 8 }}
            xl={{ span: 4 }}
            xxl={{ span: 3 }}
          >
            <AdnDropdown
              placeholder={t("offer.form.placeholders.status")}
              allowClear={true}
              defaultValue={offerStatus}
              options={[
                { id: 0, name: t("offer.form.status.notDelivered") },
                { id: 1, name: t("offer.form.status.delivered") },
                { id: 2, name: t("offer.form.status.canceled") },
              ]}
              handleChange={offerStatusHandleChange}
            />
          </Col>
          <Col
            className="filter-item"
            xs={{ span: 24 }}
            sm={{ span: 24 }}
            md={{ span: 24 }}
            lg={{ span: 16 }}
            xl={{ span: 8 }}
            xxl={{ span: 8 }}
          >
            <AdnMonthYearDropdown
              allowMonthClear
              allowYearClear
              defaultMonthValue={month !== "" ? month : null}
              defaultYearValue={year !== "" ? year : null}
              handleMonthChange={handleMonthChange}
              handleYearChange={handleYearChange}
            />
          </Col>
          <Col
            xs={{ span: 24 }}
            sm={{ span: 24 }}
            md={{ span: 24 }}
            lg={{ span: 8 }}
            xl={{ span: 16 }}
            xxl={{ span: 24 }}
          >
            <Button
              className="adn-primary-button"
              type="submit"
              onClick={handleEmployeeOffersExcel}
              style={{ float: "right" }}
            >
              <ExportOutlined /> {t("offer.form.buttons.export")}
            </Button>
          </Col>
        </Row>

        <Row>
          <Col span={24}>
            <AdnTable
              className="offersDatePicker"
              columns={columns}
              data={employeeOfferList}
              currentPage={currentPage}
              pageSize={pageSize}
              totalRecords={totalRecords}
              handleChangePage={handleChangePage}
              handleChange={handleOrderList}
              getRowClassName={getRowClassName}
              expandable={(record) => (
                <>
                  <p style={{ margin: 0 }}>
                    <b>{t("offer.form.labels.notes")} </b> {record.note}
                  </p>
                </>
              )}
            />
          </Col>
        </Row>
      </AdnCard>
      <Modal
        title={t("offer.form.modals.editOffer")}
        open={editMode}
        onOk={formOffers.submit}
        okText={t("offer.form.buttons.save")}
        cancelText={t("offer.form.buttons.cancel")}
        onCancel={CancelEditModal}
        confirmLoading={modalSubmitLoading}
      >
        <Form
          form={formOffers}
          name="form_in_modal"
          layout="vertical"
          onFinish={EditOfferHandler}
        >
          <AdnInput
            name={"offerName"}
            label={t("offer.form.labels.type")}
            placeholder={t("offer.form.placeholders.chooseOfferName")}
            readOnly={true}
            rules={NameValidation}
          />
          <AdnInput
            name={"description"}
            label={t("offer.form.labels.description")}
            readOnly={true}
            placeholder={t("offer.form.placeholders.insertDescription")}
            rules={NoteValidation}
            maxLength={60}
          />
          <AdnDatePicker
            name={"offeredAt"}
            label={t("offer.form.labels.offeredAt")}
            placeholder={t("offer.form.placeholders.selectDate")}
            disabledDate={disabledDateOffer}
            format={dateFormat}
          />
          <AdnTextArea
            name={"note"}
            label={t("offer.form.labels.note")}
            placeholder={t("offer.form.placeholders.insertNote")}
            rules={NoteValidation}
            maxLength={500}
          />
        </Form>
      </Modal>
      <Modal
        title={t("offer.form.modals.cancelOffer")}
        open={cancelOfferVisible}
        onOk={cancelOfferForm.submit}
        onCancel={() => {
          cancelOfferForm.resetFields();
          setCancelOfferVisible(false);
        }}
        footer={[
          <Button
            className="adn-outlined-button"
            key="cancel"
            onClick={() => setCancelOfferVisible(false)}
          >
            {t("offer.form.buttons.no")}
          </Button>,
          <Button
            key="ok"
            type="primary"
            onClick={cancelOfferForm.submit}
            className="adn-primary-button primary-cancel"
          >
            {t("offer.form.buttons.cancelOffer")}
          </Button>,
        ]}
      >
        <Form
          form={cancelOfferForm}
          name="form_in_modal"
          layout="vertical"
          onFinish={() => handleChangeOffer(offerFormValues, 2)}
        >
          <p> {t("offer.form.confirmations.cancelOffer")} </p>
          <p> {t("offer.form.confirmations.writeNotes")} </p>
          <AdnInput
            name={"note"}
            placeholder={t("offer.form.placeholders.insertNotes")}
            maxLength={500}
            rules={[
              {
                required: true,
                message: t("offer.form.validation.fieldRequired"),
              },
            ]}
          />
        </Form>
      </Modal>
    </>
  );
};

export default OfferListForm;
